import { memo, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import {
  isUrlFilterMultiEntry,
  isUrlFilterSlugEntry,
  parseUrlActiveFilters,
  serializeUrlActiveFilters,
  type UrlActiveFilters,
} from "@/lib/url-active-filters";
import { cn } from "@/lib/utils";
import { GSC_FILTER_KEYS, type GscFilterKey } from "@/types/gsc";

interface ActiveFilterChipsProps {
  className?: string;
}

interface ActiveFilterChip {
  key: string;
  label: string;
  value: string;
  /** Multi-select only — which value to remove. Absent for text filters. */
  multiValue?: string;
}

const FILTER_LABELS: Record<GscFilterKey, string> = {
  [GSC_FILTER_KEYS.domain]: "Domain",
  [GSC_FILTER_KEYS.country]: "Country",
  [GSC_FILTER_KEYS.device]: "Device",
  [GSC_FILTER_KEYS.query]: "Query",
  [GSC_FILTER_KEYS.slug]: "Slug",
  [GSC_FILTER_KEYS.geo]: "GEO",
  [GSC_FILTER_KEYS.site_type]: "Site Type",
  [GSC_FILTER_KEYS.brand_keyword]: "Brand",
  [GSC_FILTER_KEYS.language]: "Language",
};

const getFilterLabel = (key: string): string =>
  FILTER_LABELS[key as GscFilterKey] ?? key;

const buildChips = (filters: UrlActiveFilters): ActiveFilterChip[] => {
  const chips: ActiveFilterChip[] = [];

  for (const [key, entry] of Object.entries(filters)) {
    const label = getFilterLabel(key);

    if (isUrlFilterMultiEntry(entry)) {
      for (const value of entry.values) {
        if (!value) continue;
        chips.push({ key, label, value, multiValue: value });
      }
      continue;
    }

    if (isUrlFilterSlugEntry(entry) && entry.value.trim()) {
      chips.push({ key, label, value: entry.value.trim() });
    }
  }

  return chips;
};

const removeChipFromFilters = (
  filters: UrlActiveFilters,
  chip: ActiveFilterChip,
): UrlActiveFilters => {
  const next = { ...filters };
  const entry = next[chip.key];
  if (!entry) return next;

  if (chip.multiValue !== undefined && isUrlFilterMultiEntry(entry)) {
    const remaining = entry.values.filter((value) => value !== chip.multiValue);
    if (remaining.length === 0) {
      delete next[chip.key];
    } else {
      next[chip.key] = { ...entry, values: remaining };
    }
    return next;
  }

  delete next[chip.key];
  return next;
};

const writeFiltersAndResetPage = (
  params: URLSearchParams,
  filters: UrlActiveFilters,
): void => {
  const serialized = serializeUrlActiveFilters(filters);
  if (serialized) params.set("filters", serialized);
  else params.delete("filters");
  params.delete("page");
};

function ActiveFilterChipsComponent({ className }: ActiveFilterChipsProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const filtersParam = searchParams.get("filters");

  const activeFilters = useMemo(
    (): UrlActiveFilters => parseUrlActiveFilters(filtersParam),
    [filtersParam],
  );

  const chips = useMemo(
    (): ActiveFilterChip[] => buildChips(activeFilters),
    [activeFilters],
  );

  if (chips.length === 0) return null;

  const handleRemoveChip = (chip: ActiveFilterChip): void => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const nextFilters = removeChipFromFilters(
        parseUrlActiveFilters(prev.get("filters")),
        chip,
      );
      writeFiltersAndResetPage(next, nextFilters);
      return next;
    });
  };

  const handleClearAll = (): void => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("filters");
      next.delete("page");
      return next;
    });
  };

  return (
    <div
      className={cn("mt-4 flex flex-wrap items-center gap-2", className)}
      role="list"
      aria-label="Active filters"
    >
      <span className="text-xs font-medium text-muted-foreground tabular-nums">
        {chips.length} active
      </span>

      {chips.map((chip) => (
        <button
          key={`${chip.key}:${chip.multiValue ?? chip.value}`}
          type="button"
          role="listitem"
          onClick={() => handleRemoveChip(chip)}
          className="group inline-flex max-w-[16rem] items-center gap-1.5 rounded-full border border-primary/20 bg-primary/10 px-2.5 py-1 text-xs text-primary transition-colors hover:bg-primary/15 cursor-pointer"
          title={`Remove ${chip.label}: ${chip.value}`}
          aria-label={`Remove filter ${chip.label}: ${chip.value}`}
        >
          <span className="truncate">
            <span className="font-semibold text-primary/90">{chip.label}:</span>{" "}
            <span className="font-normal">{chip.value}</span>
          </span>
          <span className="inline-flex size-4 shrink-0 items-center justify-center rounded-full transition-colors group-hover:bg-primary/20 group-hover:text-primary-foreground">
            <X className="size-3" aria-hidden />
          </span>
        </button>
      ))}

      <button
        type="button"
        onClick={handleClearAll}
        className="rounded-md px-2 py-1 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10 cursor-pointer"
      >
        Clear all
      </button>
    </div>
  );
}

export const ActiveFilterChips = memo(ActiveFilterChipsComponent);
ActiveFilterChips.displayName = "ActiveFilterChips";
